"use client";
import React, { useState, useEffect } from "react";
import { Zap } from "lucide-react";
import classNames from "classnames";

interface BannerProps {
  data: {
    title: string;
    description: string;
    isLive: boolean;
    image1?: { data: { attributes: { url: string } } };
    image2?: { data: { attributes: { url: string } } };
  } | null;
}

export default function Banner({ data }: BannerProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    if (data?.image1?.data && data?.image2?.data) {
      const interval = setInterval(() => {
        setCurrentImageIndex((prev) => (prev === 0 ? 1 : 0));
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [data]);

  if (!data || !data.isLive) return null;

  const { title, description, image1, image2 } = data;

  const img1 = image1?.data?.attributes?.url;
  const img2 = image2?.data?.attributes?.url;
  const hasImages = !!(img1 || img2);

  /**
   * Helper Logic:
   * Splits text by brackets. Even index = normal text, Odd index = bracketed text.
   */
  const renderDynamicText = (text?: string) => {
    if (!text) return null;

    const regex = /\[\s*([^\]]+?)\s*\]/g;
    const parts: React.ReactNode[] = [];

    let lastIndex = 0;
    let match;

    while ((match = regex.exec(text)) !== null) {
      const [fullMatch, highlightedText] = match;
      const start = match.index;
      const end = start + fullMatch.length;

      // Normal text before bracket
      if (start > lastIndex) {
        parts.push(<span key={lastIndex}>{text.slice(lastIndex, start)}</span>);
      }

      // Highlighted text
      parts.push(
        <span key={start} className="text-[var(--secondary)]">
          {highlightedText.trim()}
        </span>,
      );

      lastIndex = end;
    }

    // Remaining text
    if (lastIndex < text.length) {
      parts.push(<span key={lastIndex}>{text.slice(lastIndex)}</span>);
    }

    return parts;
  };
  const dateForYear = new Date();
  return (
    <section className="relative w-full min-h-[500px] flex items-center justify-center overflow-hidden bg-neutral-900">
      {/* 1. BACKGROUND LAYER */}
      <div className="absolute inset-0 z-0">
        {!hasImages ? (
          <div className="relative h-full w-full bg-neutral-900">
            <div className="absolute top-0 right-0 w-1/3 h-full bg-[var(--secondary)] skew-x-12 translate-x-20 opacity-20 hidden lg:block" />
          </div>
        ) : (
          <>
            {img1 && (
              <div
                className={classNames(
                  "absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-1000 ease-in-out",
                  currentImageIndex === 0 || !img2
                    ? "opacity-100"
                    : "opacity-0",
                )}
                style={{ backgroundImage: `url('${img1}')` }}
              />
            )}
            {img2 && (
              <div
                className={classNames(
                  "absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-1000 ease-in-out",
                  currentImageIndex === 1 ? "opacity-100" : "opacity-0",
                )}
                style={{ backgroundImage: `url('${img2}')` }}
              />
            )}
            <div className="absolute inset-0 bg-black/10 " />
          </>
        )}
      </div>

      {/* 2. CONTENT LAYER */}
      <div className="relative z-10 w-full py-20 px-6 max-w-7xl mx-auto flex flex-col items-center text-center">
        <div className="flex items-center gap-2 mb-6">
          <Zap className="text-orange-500 fill-orange-500 h-4 w-4 animate-pulse" />
          <span className="text-white text-[10px] font-black uppercase tracking-[0.4em]">
            Sale {dateForYear.getFullYear()}
          </span>
        </div>

        {/* Dynamic Title with Bracket Logic */}
        <h2 className="text-4xl md:text-7xl lg:text-8xl font-black text-white uppercase italic tracking-tighter leading-[0.9] mb-6">
          {renderDynamicText(title)}
        </h2>

        {/* Dynamic Description with Bracket Logic */}
        <p className="text-neutral-200 text-sm md:text-lg font-bold uppercase tracking-widest mb-10 max-w-2xl leading-relaxed">
          {renderDynamicText(description)}
        </p>

        {/* Indicators */}
        {img1 && img2 && (
          <div className="flex gap-3 mt-12">
            {[0, 1].map((idx) => (
              <div
                key={idx}
                className={classNames(
                  "h-1 transition-all duration-500 rounded-full",
                  currentImageIndex === idx
                    ? "bg-[var(--secondary)] w-12"
                    : "bg-white/20 w-6",
                )}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
